/* KP ELITE — Programs collection filters, sort and drawer.
   Scoped to [data-kp-col]. No globals except window.KpCollection. */
(() => {
  'use strict';
  const SEP = '|';
  const norm = v => String(v == null ? '' : v).trim().toLowerCase();
  const list = s => String(s || '').split(SEP).map(x => x.trim()).filter(Boolean);
  let uid = 0;
  const nid = p => 'kpc-' + p + '-' + (++uid);
  const h = (tag, attrs, kids) => {
    const e = document.createElement(tag);
    if (attrs) for (const k in attrs) {
      const v = attrs[k];
      if (v == null || v === false) continue;
      if (k === 'class') e.className = v;
      else if (k === 'text') e.textContent = v;
      else if (k.slice(0, 2) === 'on') e.addEventListener(k.slice(2), v);
      else e.setAttribute(k, v === true ? '' : String(v));
    }
    if (kids != null) [].concat(kids).forEach(c => { if (c != null) e.append(c); });
    return e;
  };

  class KpCollection {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-kp-grid]');
      const cfgEl = root.querySelector('[data-kp-config]');
      if (!this.grid || !cfgEl) return;
      this.cfg = JSON.parse(cfgEl.textContent);
      this.t = this.cfg.text || {};
      this.dc = this.cfg.daysControl || 'scale';
      this.allSports = norm(this.cfg.allSports || 'All Sports');
      this.filters = (this.cfg.filters || []).map(f => {
        let opts = [];
        if (f.type === 'days') { for (let d = +f.min; d <= +f.max; d++) opts.push(String(d)); }
        else opts = String(f.options || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
        return Object.assign({}, f, { min: +f.min, max: +f.max, opts, labels: new Map(opts.map(o => [norm(o), o])) });
      }).filter(f => f.opts.length);
      this.items = Array.from(this.grid.querySelectorAll('[data-kp-item]')).map((li, i) => {
        const d = li.dataset, data = {};
        this.filters.forEach(f => { data[f.key] = list(d[f.key]).map(norm); });
        return { li, data, rank: +d.rank || i + 1, price: +d.price || 0, primary: list(d.primary).map(norm), last: parseInt(d.last, 10) || 0 };
      });
      this.sel = {};
      this.filters.forEach(f => { this.sel[f.key] = new Set(); });
      const df = this.daysF();
      this.range = df ? [df.min, df.max] : [0, 0];
      this.sort = null;
      this.openKey = null;
      this.openSecs = new Set(this.filters.length ? [this.filters[0].key] : []);
      this.updaters = [];
      this.readURL();
      this.buildBar();
      this.buildDrawer();
      this.bind();
      this.render(true);
      root.querySelector('[data-kp-bar]').hidden = false;
      this.sticky();
      if (this.cfg.chatMode === 'hide' || this.cfg.chatMode === 'drawer') document.documentElement.classList.add('kp-col-chat-' + this.cfg.chatMode);
      try { this.watchTeaser(); } catch (err) { console.warn('[KP collection] teaser', err); }
      try { this.watchChat(); } catch (err) { console.warn('[KP collection] chat', err); }
    }

    /* ---------- state helpers ---------- */
    daysF() { return this.filters.find(f => f.type === 'days'); }
    isRange(f) { return f.type === 'days' && this.dc === 'range'; }
    ctl(f) { return f.type === 'days' && this.dc !== 'list' ? this.dc : 'list'; }
    active(f) {
      if (this.isRange(f)) {
        const [a, b] = this.range;
        if (a <= f.min && b >= f.max) return [];
        const o = []; for (let d = a; d <= b; d++) o.push(String(d));
        return o;
      }
      return Array.from(this.sel[f.key]);
    }
    nActive(f) { return this.isRange(f) ? (this.active(f).length ? 1 : 0) : this.sel[f.key].size; }
    total() { return this.filters.reduce((n, f) => n + this.nActive(f), 0); }
    has(it, f, v) { const a = it.data[f.key]; return a.includes(v) || (f.key === 'sport' && a.includes(this.allSports)); }
    matches(it, skip) {
      return this.filters.every(f => {
        if (f.key === skip) return true;
        const a = this.active(f);
        return !a.length || a.some(v => this.has(it, f, v));
      });
    }
    count(f, v) { let n = 0; this.items.forEach(it => { if (this.matches(it, f.key) && this.has(it, f, v)) n++; }); return n; }
    label(f, v) { return f.type === 'days' ? v + ' ' + (this.t.days || 'Days') : (f.labels.get(v) || v); }
    daysTxt(short) { const [a, b] = this.range; return (a === b ? a : a + '–' + b) + (short ? '' : ' ' + (this.t.days || 'Days')); }
    /* Best match: primary goal +2, other tagged goal +1; single-sport match +1, all-sports match +0.5. Ties fall back to collection order. */
    score(it) {
      let s = 0;
      const g = this.filters.find(f => f.key === 'goal');
      if (g) this.active(g).forEach(v => { if (it.primary.includes(v)) s += 2; else if (it.data.goal.includes(v)) s += 1; });
      const sp = this.filters.find(f => f.key === 'sport');
      if (sp) this.active(sp).forEach(v => { if (it.data.sport.includes(v)) s += 1; else if (it.data.sport.includes(this.allSports)) s += 0.5; });
      return s;
    }
    sortKey(t) {
      if (this.sort === 'match' && !t) return 'rank';
      if (this.sort) return this.sort;
      return t && this.cfg.defaultSort === 'match' ? 'match' : 'rank';
    }
    toggle(k, v) { const s = this.sel[k]; if (s.has(v)) s.delete(v); else s.add(v); this.render(); }
    resetF(f) { if (this.isRange(f)) this.range = [f.min, f.max]; else this.sel[f.key].clear(); this.render(); }
    clear() {
      this.filters.forEach(f => this.sel[f.key].clear());
      const df = this.daysF(); if (df) this.range = [df.min, df.max];
      this.render();
    }

    /* ---------- controls ---------- */
    control(f) {
      const type = this.ctl(f);
      const wrap = h('div', { class: 'kp-col__ctl kp-col__ctl--' + type });
      if (type === 'list') {
        const rows = f.opts.map(o => {
          const v = norm(o), iid = nid('cb');
          const input = h('input', { type: 'checkbox', id: iid, class: 'kp-col__cb', value: o, onchange: () => this.toggle(f.key, v) });
          const n = h('span', { class: 'kp-col__optn' });
          const row = h('label', { class: 'kp-col__opt', for: iid }, [input, h('span', { class: 'kp-col__box', 'aria-hidden': 'true' }), h('span', { class: 'kp-col__optl', text: this.label(f, v) }), n]);
          wrap.append(row);
          return { v, input, n, row };
        });
        this.updaters.push(() => rows.forEach(r => {
          const on = this.sel[f.key].has(r.v), c = this.count(f, r.v), dis = !c && !on;
          r.input.checked = on; r.input.disabled = dis; r.row.classList.toggle('is-disabled', dis); r.n.textContent = c;
        }));
      } else if (type === 'scale') {
        const segs = f.opts.map(o => {
          const v = norm(o);
          const b = h('button', { type: 'button', class: 'kp-col__seg', 'aria-pressed': 'false', 'aria-label': this.label(f, v), onclick: () => this.toggle(f.key, v) },
            [h('b', { text: o }), h('small', { text: this.t.days || 'Days', 'aria-hidden': 'true' })]);
          wrap.append(b);
          return { v, b };
        });
        this.updaters.push(() => segs.forEach(s => {
          const on = this.sel[f.key].has(s.v), c = this.count(f, s.v);
          s.b.setAttribute('aria-pressed', String(on)); s.b.disabled = !c && !on;
        }));
      } else {
        this.rangeControl(f, wrap);
      }
      return wrap;
    }

    rangeControl(f, wrap) {
      const span = (f.max - f.min) || 1;
      const dayWord = this.t.days || 'Days';
      const val = h('span', { class: 'kp-col__rval' });
      const fill = h('span', { class: 'kp-col__rfill' });
      const mk = lab => h('span', { class: 'kp-col__rthumb', role: 'slider', tabindex: '0', 'aria-label': lab, 'aria-valuemin': f.min, 'aria-valuemax': f.max });
      const t0 = mk(this.t.min_days || 'Minimum days per week');
      const t1 = mk(this.t.max_days || 'Maximum days per week');
      const track = h('span', { class: 'kp-col__rtrack' }, [fill, t0, t1]);
      const area = h('span', { class: 'kp-col__rarea' }, track);
      const ticks = f.opts.map(o => h('span', { text: o }));
      wrap.append(h('div', { class: 'kp-col__rhead' }, [val, h('span', { class: 'kp-col__rper', text: this.t.per_week || 'per week' })]), area, h('div', { class: 'kp-col__rticks', 'aria-hidden': 'true' }, ticks));
      let thumb = null, rect = null;
      const at = e => Math.round(f.min + Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) * span);
      const set = (which, v) => {
        let [a, b] = this.range;
        v = Math.max(f.min, Math.min(f.max, v));
        if (which === 0) a = Math.min(v, b); else b = Math.max(v, a);
        if (a !== this.range[0] || b !== this.range[1]) { this.range = [a, b]; this.render(); }
      };
      area.addEventListener('pointerdown', e => {
        rect = track.getBoundingClientRect();
        const v = at(e), [a, b] = this.range;
        if (a === b && v === a) thumb = 'pending';
        else thumb = v <= a ? 0 : v >= b ? 1 : (v - a <= b - v ? 0 : 1);
        try { area.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
        (thumb === 1 ? t1 : t0).focus({ preventScroll: true });
        if (thumb !== 'pending') set(thumb, v);
        e.preventDefault();
      });
      area.addEventListener('pointermove', e => {
        if (thumb == null) return;
        const v = at(e);
        if (thumb === 'pending') { if (v === this.range[0]) return; thumb = v < this.range[0] ? 0 : 1; }
        set(thumb, v);
      });
      const end = () => { thumb = null; };
      area.addEventListener('pointerup', end);
      area.addEventListener('pointercancel', end);
      [t0, t1].forEach((t, i) => t.addEventListener('keydown', e => {
        const cur = this.range[i]; let v = null;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') v = cur - 1;
        else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') v = cur + 1;
        else if (e.key === 'Home') v = f.min;
        else if (e.key === 'End') v = f.max;
        if (v == null) return;
        e.preventDefault(); set(i, v);
      }));
      const pos = v => ((v - f.min) / span * 100) + '%';
      this.updaters.push(() => {
        const [a, b] = this.range, full = a <= f.min && b >= f.max;
        t0.style.left = pos(a); t1.style.left = pos(b);
        fill.style.left = pos(a); fill.style.width = ((b - a) / span * 100) + '%';
        t0.setAttribute('aria-valuenow', a); t1.setAttribute('aria-valuenow', b);
        t0.setAttribute('aria-valuetext', a + ' ' + dayWord); t1.setAttribute('aria-valuetext', b + ' ' + dayWord);
        val.textContent = full ? (this.t.any || 'Any') : this.daysTxt();
        ticks.forEach((s, i) => s.classList.toggle('is-on', f.min + i >= a && f.min + i <= b));
      });
    }

    /* ---------- desktop bar ---------- */
    buildBar() {
      const host = this.root.querySelector('[data-kp-filters]');
      this.dds = this.filters.filter(f => f.main).map(f => {
        const pid = nid('panel');
        const lab = h('span', { text: f.label });
        const badge = h('span', { class: 'kp-col__badge', hidden: true });
        const btn = h('button', { type: 'button', class: 'kp-col__pill', 'aria-expanded': 'false', 'aria-controls': pid },
          [lab, badge, h('span', { class: 'kp-col__chev', 'aria-hidden': 'true' })]);
        const cnt = h('span');
        const panel = h('div', { class: 'kp-col__panel', id: pid, role: 'group', 'aria-label': f.label, hidden: true }, [
          this.control(f),
          h('div', { class: 'kp-col__pfoot' }, [h('button', { type: 'button', class: 'kp-col__link', text: this.t.reset || 'Reset', onclick: () => this.resetF(f) }), cnt])
        ]);
        btn.addEventListener('click', () => this.openDD(this.openKey === f.key ? null : f.key));
        host.append(h('div', { class: 'kp-col__dd' }, [btn, panel]));
        return { f, btn, lab, badge, panel, cnt };
      });
    }
    openDD(k) {
      this.openKey = k;
      this.dds.forEach(d => {
        const on = d.f.key === k;
        d.panel.hidden = !on;
        d.btn.setAttribute('aria-expanded', String(on));
        d.btn.classList.toggle('is-open', on);
        if (on) {
          d.panel.classList.remove('kp-col__panel--right');
          if (d.panel.getBoundingClientRect().right > window.innerWidth - 8) d.panel.classList.add('kp-col__panel--right');
        }
      });
    }

    /* ---------- drawer (More filters on desktop, all filters on mobile) ---------- */
    buildDrawer() {
      const tid = nid('dtitle');
      this.closeBtn = h('button', { type: 'button', class: 'kp-col__x', 'aria-label': this.t.close || 'Close filters', onclick: () => this.setDrawer(false) }, h('span', { 'aria-hidden': 'true', text: '×' }));
      const body = h('div', { class: 'kp-col__dbody' });
      this.secs = this.filters.map(f => {
        const cid = nid('sec');
        const badge = h('span', { class: 'kp-col__badge', hidden: true });
        const sign = h('span', { class: 'kp-col__sign', 'aria-hidden': 'true' });
        const head = h('button', { type: 'button', class: 'kp-col__shead', 'aria-expanded': 'false', 'aria-controls': cid }, [h('span', { class: 'kp-col__stitle', text: f.label }), badge, sign]);
        const cont = h('div', { class: 'kp-col__sbody', id: cid, hidden: true }, this.control(f));
        head.addEventListener('click', () => { if (this.openSecs.has(f.key)) this.openSecs.delete(f.key); else this.openSecs.add(f.key); this.render(); });
        body.append(h('div', { class: 'kp-col__sec' }, [head, cont]));
        return { f, head, cont, badge, sign };
      });
      this.updaters.push(() => this.secs.forEach(s => {
        const open = this.openSecs.has(s.f.key), c = this.nActive(s.f);
        s.head.setAttribute('aria-expanded', String(open));
        s.cont.hidden = !open;
        s.sign.textContent = open ? '−' : '+';
        s.badge.hidden = !c || this.isRange(s.f);
        s.badge.textContent = c;
      }));
      this.showBtn = h('button', { type: 'button', class: 'kp-col__btn kp-col__btn--red', onclick: () => this.setDrawer(false) });
      this.panelEl = h('div', { class: 'kp-col__dpanel', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': tid }, [
        h('div', { class: 'kp-col__dhead' }, [h('h2', { class: 'kp-col__dtitle', id: tid, text: this.t.drawer_title || 'Filter Programs' }), this.closeBtn]),
        body,
        h('div', { class: 'kp-col__dfoot' }, [h('button', { type: 'button', class: 'kp-col__btn kp-col__btn--ghost', text: this.t.clear_all || 'Clear all', onclick: () => this.clear() }), this.showBtn])
      ]);
      this.drawerEl = h('div', { class: 'kp-col__drawer', hidden: true }, [h('div', { class: 'kp-col__scrim', onclick: () => this.setDrawer(false) }), this.panelEl]);
      this.root.append(this.drawerEl);
    }
    setDrawer(open) {
      if (open) {
        this._ret = document.activeElement;
        this.openDD(null);
        this._openedAt = Date.now();
      }
      this.drawerEl.hidden = !open;
      document.documentElement.classList.toggle('kp-col-lock', open);
      if (open) {
        this.closeBtn.focus();
        if (this.cfg.chatMode === 'drawer') { try { this.hideFloaters(); } catch (err) { console.warn('[KP collection] chat hide', err); } }
      } else {
        if (this.cfg.chatMode === 'drawer') { try { this.showFloaters(); } catch (err) { console.warn('[KP collection] chat show', err); } }
        if (this._ret && this._ret.focus) this._ret.focus();
      }
    }

    /* ---------- floating widgets (chat bubbles, discount pills) ---------- */
    up(n) { return n.parentElement || (n.getRootNode && n.getRootNode() !== document && n.getRootNode().host) || null; }
    nearestFixed(el) {
      for (let n = el; n && n.nodeType === 1 && n !== document.body && n !== document.documentElement; n = this.up(n)) {
        if (getComputedStyle(n).position === 'fixed') return n;
      }
      return null;
    }
    isSmall(el, maxW, maxH) { const r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0 && r.width <= maxW && r.height <= maxH; }
    /* Never touch the page, the drawer, or anything large. */
    safe(el) {
      if (!el || el.nodeType !== 1 || el === document.body || el === document.documentElement) return false;
      if (this.root.contains(el) || el.contains(this.root)) return false;
      const r = el.getBoundingClientRect();
      return r.width <= 440 && r.height <= 200;
    }
    findChat() {
      const out = new Set();
      const sels = ['shopify-chat', 'inbox-online-store-chat', '#shopify-chat', '#ShopifyChat', '#dummy-chat-button-iframe', 'iframe[title*="chat" i]', 'iframe[id*="chat" i]', '[id*="chat-widget" i]', '[class*="chat-widget" i]']
        .concat(String(this.cfg.hideSelectors || '').split(/[\n,]+/).map(s => s.trim()).filter(Boolean));
      sels.forEach(sel => {
        try { document.querySelectorAll(sel).forEach(el => { const f = this.nearestFixed(el); out.add(f && this.safe(f) ? f : el); }); } catch (_) { /* invalid selector */ }
      });
      try {
        const res = document.evaluate('//body//*[not(self::script) and not(self::style)][normalize-space(.)="Chat"]', document, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);
        for (let i = 0; i < res.snapshotLength; i++) {
          const f = this.nearestFixed(res.snapshotItem(i));
          if (f && this.isSmall(f, 420, 160)) out.add(f);
        }
      } catch (_) { /* no xpath */ }
      if (document.elementsFromPoint) {
        const vw = window.innerWidth, vh = window.innerHeight;
        [[vw - 40, vh - 40], [vw - 70, vh - 30], [vw - 30, vh - 70]].forEach(([x, y]) => {
          document.elementsFromPoint(x, y).forEach(el => {
            if (el.nodeType !== 1 || this.root.contains(el)) return;
            const f = this.nearestFixed(el);
            if (f) { if (this.isSmall(f, 420, 160)) out.add(f); }
            else if (el.tagName.includes('-') && el.shadowRoot) out.add(el);
          });
        });
      }
      return Array.from(out).filter(el => this.safe(el) || (/^(shopify-chat|inbox-online-store-chat)$/i.test(el.tagName) && !el.contains(this.root)));
    }
    hideFloaters() {
      this._hidden = this._hidden || [];
      this.findChat().forEach(el => {
        if (this._hidden.some(x => x.el === el)) return;
        const rec = { el, d: el.style.getPropertyValue('display'), dp: el.style.getPropertyPriority('display'), o: el.style.opacity, t: el.style.transition };
        this._hidden.push(rec);
        el.style.transition = 'opacity .12s ease';
        el.style.setProperty('opacity', '0', 'important');
        rec.timer = setTimeout(() => { if (this._hidden.includes(rec)) el.style.setProperty('display', 'none', 'important'); }, 120);
      });
    }
    showFloaters() {
      (this._hidden || []).forEach(rec => {
        const el = rec.el;
        clearTimeout(rec.timer);
        if (rec.d) el.style.setProperty('display', rec.d, rec.dp); else el.style.removeProperty('display');
        el.style.removeProperty('opacity');
        if (rec.o) el.style.opacity = rec.o;
        el.style.transition = rec.t || '';
      });
      this._hidden = [];
    }
    watchChat() {
      if (this.cfg.chatMode !== 'hide' || !window.MutationObserver) return;
      const scan = () => {
        this._cq = 0;
        try { this.findChat().forEach(el => { if (!el.__kpChat) { el.__kpChat = true; el.style.setProperty('display', 'none', 'important'); } }); }
        catch (err) { console.warn('[KP collection] chat', err); }
      };
      new MutationObserver(() => { if (!this._cq) this._cq = setTimeout(scan, 80); }).observe(document.body, { childList: true, subtree: true });
      scan(); setTimeout(scan, 1500); setTimeout(scan, 4000);
    }
    /* On this page only: once a visitor closes the discount popup, its little reminder pill is not shown.
       Matches a small fixed element containing the text set in the customizer. The popup itself is untouched. */
    watchTeaser() {
      const text = String(this.cfg.teaserText || '').trim();
      if (!text || !window.MutationObserver || !document.evaluate) return;
      const lit = '"' + text.replace(/"/g, '') + '"';
      this._pills = [];
      const scan = () => {
        this._tq = 0;
        let res;
        try { res = document.evaluate('//body//*[not(self::script) and not(self::style)][contains(normalize-space(text()), ' + lit + ')]', document, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null); } catch (_) { return; }
        for (let i = 0; i < res.snapshotLength; i++) {
          const el = res.snapshotItem(i);
          if (this.root.contains(el)) continue;
          const f = this.nearestFixed(el);
          if (f && !f.__kpHidden && this.isSmall(f, 520, 130)) { f.__kpHidden = true; this._pills.push(f); }
        }
        const mobile = !this.cfg.teaserMobileOnly || window.innerWidth < 750;
        this._pills.forEach(p => {
          if (mobile && !p.__kpOff) { p.__kpOff = true; p.style.setProperty('display', 'none', 'important'); }
          else if (!mobile && p.__kpOff) { p.__kpOff = false; p.style.removeProperty('display'); }
        });
      };
      new MutationObserver(() => { if (!this._tq) this._tq = setTimeout(scan, 60); }).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'open', 'aria-hidden'] });
      scan();
      window.addEventListener('resize', () => { if (!this._tq) this._tq = setTimeout(scan, 150); });
    }
    trap(e) {
      const els = Array.from(this.panelEl.querySelectorAll('button, input, select, [tabindex="0"]')).filter(x => !x.disabled && x.offsetParent !== null);
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    bind() {
      const q = s => this.root.querySelector(s);
      this.root.querySelectorAll('[data-kp-open]').forEach(b => b.addEventListener('click', () => this.setDrawer(true)));
      this.root.querySelectorAll('[data-kp-clear]').forEach(b => b.addEventListener('click', () => this.clear()));
      this.sortSel = q('[data-kp-sort]');
      this.matchOpt = this.sortSel.querySelector('option[value="match"]');
      this.sortSel.addEventListener('change', () => { this.sort = this.sortSel.value; this.render(); });
      this.counts = this.root.querySelectorAll('[data-kp-count]');
      this.tagsEl = q('[data-kp-tags]');
      this.emptyEl = q('[data-kp-empty]');
      this.mLabel = q('[data-kp-mlabel]');
      this.moreBadge = q('[data-kp-more-badge]');
      document.addEventListener('click', e => {
        if (this.openKey && !(this.root.contains(e.target) && e.target.closest('.kp-col__dd'))) this.openDD(null);
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          if (!this.drawerEl.hidden) this.setDrawer(false);
          else if (this.openKey) { const d = this.dds.find(x => x.f.key === this.openKey); this.openDD(null); if (d) d.btn.focus(); }
        } else if (e.key === 'Tab' && !this.drawerEl.hidden) this.trap(e);
      });
      window.addEventListener('resize', () => { if (this.openKey && window.innerWidth < 990) this.openDD(null); });
      document.addEventListener('click', e => {
        if (this.drawerEl.hidden || !this._openedAt || Date.now() - this._openedAt < 50) return;
        const path = e.composedPath ? e.composedPath() : [];
        if (path.includes(this.panelEl)) return;
        if (e.target.closest && e.target.closest('[data-kp-open]')) return;
        this.setDrawer(false);
      });
    }

    /* ---------- render ---------- */
    render(first) {
      const t = this.total();
      const vis = new Set(this.items.filter(it => this.matches(it)));
      const key = this.sortKey(t);
      let cmp;
      if (key === 'match') {
        const sc = new Map(); vis.forEach(it => sc.set(it, this.score(it)));
        cmp = (a, b) => (a.last - b.last) || (sc.get(b) || 0) - (sc.get(a) || 0) || a.rank - b.rank;
      } else if (key === 'low') cmp = (a, b) => a.price - b.price || a.rank - b.rank;
      else if (key === 'high') cmp = (a, b) => b.price - a.price || a.rank - b.rank;
      else cmp = (a, b) => (a.last - b.last) || a.rank - b.rank;
      const ordered = this.items.slice().sort((a, b) => (vis.has(b) - vis.has(a)) || cmp(a, b));
      const cur = this.grid.children;
      if (ordered.some((it, i) => cur[i] !== it.li)) ordered.forEach(it => this.grid.append(it.li));
      this.items.forEach(it => { it.li.hidden = !vis.has(it); });

      const n = vis.size;
      const lbl = n + ' ' + (n === 1 ? (this.t.program || 'program') : (this.t.programs || 'programs'));
      this.counts.forEach(c => { c.textContent = lbl; });
      this.showBtn.textContent = (this.t.show || 'Show') + ' ' + lbl;
      this.emptyEl.hidden = n > 0;

      if (t && !this.matchOpt.parentNode) this.sortSel.prepend(this.matchOpt);
      if (!t && this.matchOpt.parentNode) this.matchOpt.remove();
      this.sortSel.value = key;

      this.mLabel.textContent = t ? (this.t.edit_filters || 'Edit Filters') + ' (' + t + ')' : (this.t.mobile_button || 'Filter');
      const more = this.filters.filter(f => !f.main).reduce((a, f) => a + this.nActive(f), 0);
      this.moreBadge.hidden = !more;
      this.moreBadge.textContent = more;
      this.dds.forEach(d => {
        const c = this.nActive(d.f), r = this.isRange(d.f);
        d.lab.textContent = r && c ? d.f.label + ': ' + this.daysTxt(true) : d.f.label;
        d.badge.hidden = !c || r;
        d.badge.textContent = c;
        d.btn.classList.toggle('is-active', c > 0);
        d.cnt.textContent = lbl;
      });
      this.updaters.forEach(u => u());
      this.renderTags(t);
      if (!first) this.writeURL();
    }
    tag(f, val, fn) {
      return h('button', { type: 'button', class: 'kp-col__tag', 'aria-label': (this.t.remove || 'Remove filter') + ': ' + f.label + ' ' + val, onclick: fn },
        [h('span', { class: 'kp-col__tag-g', text: f.label + ':' }), h('span', { text: val }), h('span', { class: 'kp-col__tag-x', 'aria-hidden': 'true', text: '×' })]);
    }
    renderTags(t) {
      const el = this.tagsEl;
      el.textContent = '';
      this.filters.forEach(f => {
        if (this.isRange(f)) {
          if (this.nActive(f)) el.append(this.tag(f, this.daysTxt(), () => { this.range = [f.min, f.max]; this.render(); }));
          return;
        }
        this.sel[f.key].forEach(v => el.append(this.tag(f, this.label(f, v), () => this.toggle(f.key, v))));
      });
      if (t) el.append(h('button', { type: 'button', class: 'kp-col__link kp-col__clear', text: this.t.clear_all || 'Clear all', onclick: () => this.clear() }));
      el.hidden = !t;
    }

    /* ---------- URL state (replaceState only; canonical stays the clean collection URL) ---------- */
    readURL() {
      let p;
      try { p = new URLSearchParams(window.location.search); } catch (_) { return; }
      this.filters.forEach(f => {
        const raw = p.get(f.key);
        if (!raw) return;
        if (this.isRange(f)) {
          const m = raw.split('-').map(Number).filter(x => x >= f.min && x <= f.max);
          if (m.length === 2 && m[0] <= m[1]) this.range = m;
          else if (m.length === 1) this.range = [m[0], m[0]];
          return;
        }
        raw.split(',').map(norm).filter(Boolean).forEach(v => this.sel[f.key].add(v));
      });
      const s = p.get('sort');
      if (['match', 'rank', 'low', 'high'].includes(s)) this.sort = s;
    }
    writeURL() {
      if (!window.history || !history.replaceState) return;
      const u = new URL(window.location.href);
      this.filters.forEach(f => u.searchParams.delete(f.key));
      u.searchParams.delete('sort');
      this.filters.forEach(f => {
        if (this.isRange(f)) {
          if (this.nActive(f)) u.searchParams.set(f.key, this.range[0] === this.range[1] ? String(this.range[0]) : this.range.join('-'));
        } else if (this.sel[f.key].size) {
          u.searchParams.set(f.key, Array.from(this.sel[f.key]).map(v => f.type === 'days' ? v : this.label(f, v)).join(','));
        }
      });
      if (this.sort) u.searchParams.set('sort', this.sort);
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    }

    /* Keep the filter bar tucked under the theme's sticky header (if any). */
    sticky() {
      const hdr = ['.section-header', 'sticky-header', 'header'].map(s => document.querySelector(s)).find(Boolean);
      if (!hdr) return;
      let raf = 0;
      const upd = () => { raf = 0; const r = hdr.getBoundingClientRect(); this.root.style.setProperty('--kp-col-top', Math.max(0, Math.round(r.bottom)) + 'px'); };
      window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(upd); }, { passive: true });
      window.addEventListener('resize', upd);
      upd();
    }
  }

  const init = root => {
    if (root.__kpCol) return;
    try { root.__kpCol = new KpCollection(root); } catch (err) { console.error('[KP collection]', err); }
  };
  const boot = () => document.querySelectorAll('[data-kp-col]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', e => { const r = e.target.querySelector('[data-kp-col]'); if (r) init(r); });
  document.addEventListener('shopify:section:unload', () => document.documentElement.classList.remove('kp-col-lock'));
  window.KpCollection = KpCollection;
})();
